import type { InstanceSharePolicy, ShareChannelPolicy } from "@keydials/api-client";
import type { IconName } from "@keydials/dial-workspace";
import { HELP_ANCHORS } from "@keydials/ui";

/**
 * Which sections the Dial Settings page offers, and which one a URL resolves to (KEY-455).
 *
 * A pure module for the same reason web-admin's `crmNav.ts` and `billingNav.ts` are: the decisions here —
 * who sees the Settings button at all, what a stale or forbidden `:section` in the URL falls back to — are
 * exactly the ones worth testing, and testing them through a rendered page would need a DOM harness
 * web-user does not have.
 *
 * Every dial setting now arrives through here. `roles`, `feeds` and `structure` were the last three
 * behind their own top-bar buttons; with those moved, the dial's chrome carries no settings at all and
 * the tab is the single answer to "where do I change this dial?".
 */
export type DialSettingsSection =
  | "members"
  | "whoswho"
  | "contacts"
  | "calendars"
  | "guests"
  | "licensing"
  | "family"
  | "roles"
  | "structure"
  | "display"
  | "device"
  | "feeds"
  | "insights";

export interface DialSettingsItem {
  id: DialSettingsSection;
  label: string;
  icon: IconName;
  /** Shown under the label in the menu — what this section is for, in the user's terms. */
  hint: string;
  /** Help-tour target, for a section a guided tour needs to point at by name. */
  helpAnchorId?: string;
}

export interface DialSettingsGates {
  /**
   * May see who is on this dial. Deliberately wider than `isDialAdmin`: the old Members button was
   * ungated and showed a read-only list to everybody, and narrowing that to admins would quietly take
   * away an affordance people use to find out who else is working on a dial.
   */
  canSeeMembers: boolean;
  /** Holds a whole-dial Admin grant. Gate for anything that changes the dial for everybody on it. */
  isDialAdmin: boolean;
  /** The definition offers a shared-device mode (`detail.autoLock` is non-null). */
  hasAutoLock: boolean;
  /** AI insights are switched on for this dial. */
  insightsEnabled: boolean;
  /** KEY-PEOPLE: the dial's Who's who list is switched on (Features:DialPeople). */
  peopleEnabled?: boolean;
  /** KEY-736: the dial has a rolling-days section, so it can show people's calendars. */
  hasDaySections?: boolean;
  /** The structure editor is open to this caller on this dial (the server's `canCustomise`). */
  canEditStructure: boolean;
  /**
   * It isn't — but this caller is a dial admin who should read *why* rather than find nothing (KEY-239).
   * Kept separate from {@link canEditStructure} because the entry appears either way: the difference is
   * whether it opens the editor or the explanation.
   */
  hasStructureNotice: boolean;
  /**
   * Manages instances for the organisation (the `instance.manage` capability).
   *
   * Only used to widen Licensing (KEY-497), and it has to be its own gate rather than folded into
   * {@link isDialAdmin}: the server answers the seat endpoint for anyone who can manage this dial's
   * members, which is a dial Admin **or** an org instance-manager, and an org manager who holds no Admin
   * grant on this particular dial would otherwise be shown no entry onto a page the API happily serves.
   */
  managesInstances: boolean;
  /**
   * This dial is a GROUP (KEY-528) — it has no content of its own, only children.
   *
   * The first group/child distinction in these settings: every other gate here asks what this CALLER may
   * do, and this one asks what the DIAL is. Structure is meaningless on a group and was being offered
   * anyway, so a group admin could rename parts that a child's own structure immediately overrides — that
   * one is hidden outright. Status roles is a property of the children too, but hiding it left an admin
   * with no way to tell "not here" from "not for you" (KEY-641), so it stays listed and points at the
   * subdials instead.
   *
   * Deliberately narrow. Feeds stays on a group — a group can be fed and can feed — and so does everything
   * about who may open it, which is a real question at group level.
   */
  isGroup: boolean;
  /**
   * Who may hand this dial out (KEY-546), from `detail.sharePolicy`. Absent from older payloads, which
   * behave exactly as before.
   *
   * It changes hints, never the menu. Guests and Feeds stay listed when their channel is shut: Guests
   * still lists and revokes the guests already invited, and half of Feeds — what other dials send this
   * one — isn't the sharer's side at all. Hiding either would take away something that still works.
   */
  sharePolicy?: InstanceSharePolicy | null;
  /**
   * The caller bought this dial's organisation a family pack (KEY-651) and may manage its places — the
   * family endpoint answered with a pack for their OWN organisation, which it only does for billing.read.
   *
   * Lives on the dial rather than in Your settings because the pack is about the dial: each relative gets
   * one of their own like it. Never on a customer's dial opened by a partner admin — `Workspace` only asks
   * when the dial belongs to the caller's own organisation, for the reason plan switching is theirs alone.
   * Optional so older callers and tests read it as "no pack".
   */
  hasFamilyPack?: boolean;
}

/**
 * The menu hint for a shut channel, or null when it's open. Only for a dial admin: `allowed` is the policy
 * alone, and a Reader told "your organisation has closed this" would be blamed for a door they never had.
 */
function blockedHint(
  gates: DialSettingsGates,
  answer: ShareChannelPolicy | undefined,
  closed: string,
  managerOnly: string,
  testDial: string = closed,
): string | null {
  if (!gates.isDialAdmin || !answer || answer.allowed) return null;
  // KEY-560: the dial, not the organisation, is the reason, so the hint must not blame the organisation.
  if (answer.blockReason === "CheckingTestDial") return testDial;
  return answer.blockReason === "OrganisationManagerOnly" ? managerOnly : closed;
}

/**
 * The menu, in display order, filtered to what this caller may actually open.
 *
 * An empty result is meaningful: it means this person has no dial settings at all, so the Settings button
 * should not be offered. Callers should branch on `.length`, never render a settings entry point
 * unconditionally — a button that opens an empty page is worse than no button.
 */
export function dialSettingsItems(gates: DialSettingsGates): DialSettingsItem[] {
  const items: DialSettingsItem[] = [];

  // Members leads: it is the setting people come here for, and the only one that changes who can see the
  // dial at all. Offered to anyone who can read the member list, read-only for those who can't change it.
  if (gates.canSeeMembers) {
    items.push({
      id: "members",
      label: "People",
      icon: "users",
      hint:
        blockedHint(
          gates,
          gates.sharePolicy?.member,
          "Who's on this dial — your organisation has closed adding people",
          "Who's on this dial — only an organisation administrator can add people",
          "Who's on this dial — a test dial for a dial you built is yours alone",
        ) ?? "Who can open this dial, and what they can do",
      helpAnchorId: HELP_ANCHORS.dialPeople,
    });
  }

  // KEY-PEOPLE: the people the dial is ABOUT, as opposed to People above, who can open it. Right after
  // People because the two meet (a name here can be linked to a member there), and admin-only because it
  // is a list of names the dial's fields pick from, not a read of who is around.
  if (gates.peopleEnabled && gates.isDialAdmin) {
    items.push({
      id: "whoswho",
      label: "Who’s who",
      icon: "users",
      hint: "The people this dial keeps track of, whether or not they can sign in",
    });
  }

  // KEY-752: who the dial DEALS with — the dentist, the school office — saved once with a number and an
  // address, and picked by "With" fields. Next to Who's who because both are lists the dial's fields pick
  // from, behind the same flag and the same admin bar.
  if (gates.peopleEnabled && gates.isDialAdmin) {
    items.push({
      id: "contacts",
      label: "Contacts",
      icon: "bookmark",
      hint: "Dentists, doctors, schools — saved once, picked wherever they come up",
    });
  }

  // KEY-736: whose calendars this dial shows. For everyone on it, not only admins: each person adds their OWN
  // calendar and decides whether others see its titles, and a reader should be able to see whose diary it is.
  if (gates.hasDaySections) {
    items.push({
      id: "calendars",
      label: "Calendars",
      icon: "calendar",
      hint: "Whose diaries this dial shows the coming week from",
      helpAnchorId: HELP_ANCHORS.dialCalendars,
    });
  }

  // Guests are their own item rather than a tab under People: a different population with different
  // rules — no organisation membership, no licence seat, and an expiry. KEY-454 moved People onto this
  // page and left guests behind in a modal nothing mounted any more, which is how they became
  // unreachable; keeping them at the same level as People is what stops that recurring.
  if (gates.isDialAdmin) {
    items.push({
      id: "guests",
      label: "Guests",
      icon: "lock",
      hint:
        blockedHint(
          gates,
          gates.sharePolicy?.guest,
          "Your organisation has closed guest invites",
          "Only an organisation administrator can invite guests",
          "A test dial can't have guests",
        ) ?? "Time-limited access for people outside the organisation",
      helpAnchorId: HELP_ANCHORS.dialGuests,
    });
  }

  // What the licence allows, and how much of it is left (KEY-497). Sits after Guests because it is the
  // commercial answer to the same question those two ask — who can be on this dial — and because Guests is
  // where somebody lands when the answer is "no places left, invite them as a guest instead".
  //
  // Gated to match the server exactly. The seat endpoint answers for whoever may manage this dial's
  // members and 404s for everybody else, so a narrower gate would hide a readable page and a wider one
  // would offer an entry onto an empty error.
  if (gates.isDialAdmin || gates.managesInstances) {
    items.push({
      id: "licensing",
      label: "Licensing",
      icon: "lock",
      hint: "How many people your licence covers, and how many are using it",
      helpAnchorId: HELP_ANCHORS.dialLicensing,
    });
  }

  // A family pack's places (KEY-651). Directly after Licensing: both answer "who else can have this dial",
  // one inside this organisation and one in organisations of their own. Not on a group — a pack buys
  // dials, and a group is never the dial a relative gets a copy of.
  if (gates.hasFamilyPack && !gates.isGroup) {
    items.push({
      id: "family",
      label: "Family",
      icon: "users",
      hint: "Invite your family to a dial of their own",
    });
  }

  // Who may do what, once they are in. Sits with People and Guests because all three answer "who", and
  // apart from Display and Device, which answer "how this dial looks and behaves".
  //
  // Stays listed on a group (KEY-641), unlike Structure below: a group's statuses ARE rolled up from its
  // children and never set on it, so hiding the entry would be correct by the same logic that hides
  // Structure — but David hit that silence twice and had no way to tell "not here" from "not for you".
  // The entry now points at the subdials instead of opening a page of its own.
  if (gates.isDialAdmin) {
    items.push({
      id: "roles",
      label: "Status roles",
      icon: "flag",
      hint: gates.isGroup ? "Set on each subdial" : "Which roles can move each status, upload, and post",
    });
  }

  // What the dial contains. Offered to a dial admin even when editing is gated off, because the reason it
  // is off is the thing they came here to find out — an entry that explains itself beats no entry at all.
  //
  // Not on a group (KEY-528). The exception above does not extend here: it exists so an admin can find out
  // WHY editing is off for a dial that could otherwise have it, and every reason it reports —
  // version envelope, master switch, permission — is about a dial with content of its own. A group's face
  // is its children's, so there is no gate to explain, only the wrong place to be looking.
  if (!gates.isGroup && (gates.canEditStructure || gates.hasStructureNotice)) {
    items.push({
      id: "structure",
      label: "Structure",
      icon: "layers",
      hint: "Add, hide or rename the parts of this dial",
      helpAnchorId: HELP_ANCHORS.dialEditStructure,
    });
  }

  // What everyone on the dial is shown, as opposed to what any one person then chooses.
  if (gates.isDialAdmin) {
    items.push({
      id: "display",
      label: "Display",
      icon: "target",
      hint: "This dial's name, what it shows, and what people can filter out",
    });
  }

  // Shared device and time zone are one item: both answer "how does this dial behave on the floor",
  // and splitting them would leave a one-control page behind each.
  if (gates.isDialAdmin) {
    items.push({
      id: "device",
      label: gates.hasAutoLock ? "Shared device & time zone" : "Time zone",
      icon: "clock",
      hint: gates.hasAutoLock
        ? "Lock this dial to a tablet, and set the clock it keeps"
        : "The clock this dial keeps",
    });
  }

  // Cross-organisation sharing. Last of the admin settings: it is the only one whose blast radius leaves
  // this organisation, so it reads better after the ones that don't.
  if (gates.isDialAdmin) {
    items.push({
      id: "feeds",
      label: "Feeds",
      icon: "share",
      hint:
        blockedHint(
          gates,
          gates.sharePolicy?.feed,
          "What this dial receives — your organisation has closed feeding it out",
          "What this dial receives — only an organisation administrator can feed it out",
          "A test dial can't be fed to or from other organisations",
        ) ?? "What this dial sends to, and receives from, other organisations",
    });
  }

  // Not gated on isDialAdmin: insights are a read of the dial, offered to whoever the feature is on for.
  if (gates.insightsEnabled) {
    items.push({
      id: "insights",
      label: "AI insights",
      icon: "sparkles",
      hint: "An advisory read of this dial's current state",
    });
  }

  return items;
}

/**
 * The section a URL should land on: the one asked for when it exists and is permitted, otherwise the first
 * item the caller may open. Null when there is nothing to show — the caller should send them back to the
 * dial rather than render an empty shell.
 *
 * Falling back rather than 404-ing matters because these URLs get bookmarked and shared: a link to a
 * section that has since moved, been renamed, or become forbidden should still open something useful.
 */
export function resolveSection(
  requested: string | undefined,
  items: DialSettingsItem[],
): DialSettingsSection | null {
  if (items.length === 0) {
    return null;
  }
  const match = items.find((i) => i.id === requested);
  return match ? match.id : items[0].id;
}
